import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import { PrinterOutlined, CloseOutlined } from '@ant-design/icons';
import moment from 'moment';
import './PrintPreview.css';

/**
 * PrintPreview Component
 * A reusable component that displays JSON data in a print-ready format
 * Can be opened in a new tab/window for printing
 * 
 * Usage:
 * 1. Direct rendering in a modal/page
 * 2. Opening in a new window using openPrintPreview helper
 * 
 * @param {Object} data - The JSON data to display
 * @param {String} title - Title of the document
 * @param {Object} headerConfig - Custom header configuration
 * @param {Array} sections - Optional: Structured sections for organized display
 * @param {Boolean} autoFormat - Auto-format JSON data into readable sections
 * @param {Boolean} showPrintButton - Show print button (default: true)
 * @param {Boolean} showCloseButton - Show close button for new window (default: true)
 * @param {Function} onClose - Callback when close button is clicked
 */
const PrintPreview = ({
    data = {},
    title = 'Document',
    headerConfig = {
        hospitalName: 'Prakruti Ayurvedic Health Center & Hospital',
        subtitle: 'A Project By Prakruti Ayurvedic Health Resort, Satara',
        address: 'Satara, Maharashtra, India',
        phone: '+91 9175242908',
        email: 'feelbetter@prakruti.health',
        website: 'www.prakruti.health',
        logo: '/prakruti-logo.png',
    },
    sections = null,
    autoFormat = true,
    showPrintButton = true,
    showCloseButton = true,
    onClose = null,
}) => {

    useEffect(() => {
        // Add print-preview class to body when component mounts
        document.body.classList.add('print-preview-active');

        return () => {
            document.body.classList.remove('print-preview-active');
        };
    }, []);

    /**
     * Format value for display
     */
    const formatValue = (value) => {
        if (value === null || value === undefined || value === '') {
            return '—';
        }

        // Handle arrays
        if (Array.isArray(value)) {
            return value.length > 0 ? value.join(', ') : '—';
        }

        // Handle objects (nested data)
        if (typeof value === 'object') {
            return JSON.stringify(value, null, 2);
        }

        // Handle dates
        if (moment(value, moment.ISO_8601, true).isValid()) {
            return moment(value).format('DD MMM YYYY, hh:mm A');
        }

        // Handle booleans
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }

        return value.toString();
    };

    /**
     * Convert camelCase or snake_case to Title Case
     */
    const formatLabel = (key) => {
        return key
            .replace(/([A-Z])/g, ' $1') // Add space before capital letters
            .replace(/_/g, ' ') // Replace underscores with spaces
            .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
            .trim();
    };

    /**
     * Auto-generate sections from flat JSON data
     */
    const autoGenerateSections = () => {
        const entries = Object.entries(data);

        if (entries.length === 0) {
            return [{
                title: 'No Data',
                fields: []
            }];
        }

        // Group fields into sections (every 10 fields or by logical grouping)
        const sectionSize = 10;
        const generatedSections = [];

        for (let i = 0; i < entries.length; i += sectionSize) {
            const sectionFields = entries.slice(i, i + sectionSize);
            generatedSections.push({
                title: `Section ${Math.floor(i / sectionSize) + 1}`,
                fields: sectionFields.map(([key, value]) => ({
                    label: formatLabel(key),
                    value: formatValue(value),
                    key: key
                }))
            });
        }

        return generatedSections;
    };

    /**
     * Render sections
     */
    const renderSections = () => {
        const sectionsToRender = sections || (autoFormat ? autoGenerateSections() : null);

        if (!sectionsToRender) {
            // Render raw JSON
            return (
                <div className="print-preview-raw-data">
                    <pre>{JSON.stringify(data, null, 2)}</pre>
                </div>
            );
        }

        return sectionsToRender.map((section, index) => {
            if (section.type === 'table') {
                return (
                    <div key={index} className="print-preview-section">
                        <h3 className="print-preview-section-title">
                            {section.title}
                        </h3>
                        {section.description && (
                            <p className="print-preview-section-description">{section.description}</p>
                        )}
                        <div className="print-preview-section-content">
                            <table className="print-preview-table print-preview-data-table">
                                <thead>
                                    <tr>
                                        {section.headers.map((header, i) => <th key={i}>{header}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {section.data.map((row, rowIndex) => (
                                        <tr key={rowIndex}>
                                            {row.map((cell, cellIndex) => (
                                                <td key={cellIndex}>{cell}</td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            }

            return (
                <div key={index} className="print-preview-section">
                    <h3 className="print-preview-section-title">
                        {section.title}
                    </h3>
                    {section.description && (
                        <p className="print-preview-section-description">{section.description}</p>
                    )}
                    <div className="print-preview-section-content">
                        <table className="print-preview-table">
                            <tbody>
                                {section.fields.map((field, fieldIndex) => (
                                    <tr key={fieldIndex}>
                                        <td className="print-preview-label">
                                            <strong>{field.label}</strong>
                                        </td>
                                        <td className="print-preview-value">
                                            {field.value}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        });
    };

    /**
     * Handle print
     */
    const handlePrint = () => {
        window.print();
    };

    /**
     * Handle close
     */
    const handleClose = () => {
        if (onClose) {
            onClose();
        } else if (window.opener) {
            // If opened in new window, close it
            window.close();
        }
    };

    return (
        <div className="print-preview-container">
            {/* Action Buttons - Hidden in print */}
            <div className="print-preview-actions no-print">
                {showPrintButton && (
                    <Button
                        type="primary"
                        icon={<PrinterOutlined />}
                        onClick={handlePrint}
                        size="large"
                    >
                        Print Document
                    </Button>
                )}
                {showCloseButton && (
                    <Button
                        icon={<CloseOutlined />}
                        onClick={handleClose}
                        size="large"
                        style={{ marginLeft: '10px' }}
                    >
                        Close
                    </Button>
                )}
            </div>

            {/* Print Content */}
            <div className="print-preview-content">
                {/* Header */}
                <div className="print-preview-header">
                    {headerConfig.logo && (
                        <div className="print-preview-logo">
                            <img src={headerConfig.logo} alt="Logo" />
                        </div>
                    )}
                    <div className="print-preview-header-content">
                        <h1 className="print-preview-hospital-name">
                            {headerConfig.hospitalName}
                        </h1>
                        {headerConfig.subtitle && (
                            <p className="print-preview-subtitle">{headerConfig.subtitle}</p>
                        )}
                        <div className="print-preview-contact-info">
                            {headerConfig.address && (
                                <span className="print-preview-contact-item">
                                    📍 {headerConfig.address}
                                </span>
                            )}
                            {headerConfig.phone && (
                                <span className="print-preview-contact-item">
                                    📞 {headerConfig.phone}
                                </span>
                            )}
                            {headerConfig.email && (
                                <span className="print-preview-contact-item">
                                    ✉️ {headerConfig.email}
                                </span>
                            )}
                            {headerConfig.website && (
                                <span className="print-preview-contact-item">
                                    🌐 {headerConfig.website}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="print-preview-divider"></div>

                {/* Document Title */}
                <div className="print-preview-title-section">
                    <h2 className="print-preview-document-title">{title}</h2>
                    <p className="print-preview-print-date">
                        Printed on: {moment().format('DD MMMM YYYY, hh:mm A')}
                    </p>
                </div>

                {/* Body - Sections */}
                <div className="print-preview-body">
                    {renderSections()}
                </div>

                {/* Footer */}
                <div className="print-preview-footer">
                    <div className="print-preview-signature-section">
                        <div className="print-preview-signature-box">
                            <div className="print-preview-signature-line"></div>
                            <p>Authorized Signature</p>
                        </div>
                        <div className="print-preview-signature-box">
                            <div className="print-preview-signature-line"></div>
                            <p>Date & Time</p>
                        </div>
                    </div>
                    <div className="print-preview-footer-branding">
                        <p>This is a computer-generated document from {headerConfig.hospitalName}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

PrintPreview.propTypes = {
    data: PropTypes.object.isRequired,
    title: PropTypes.string,
    headerConfig: PropTypes.shape({
        hospitalName: PropTypes.string,
        subtitle: PropTypes.string,
        address: PropTypes.string,
        phone: PropTypes.string,
        email: PropTypes.string,
        website: PropTypes.string,
        logo: PropTypes.string,
    }),
    sections: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string.isRequired,
            description: PropTypes.string,
            fields: PropTypes.arrayOf(
                PropTypes.shape({
                    label: PropTypes.string.isRequired,
                    value: PropTypes.any.isRequired,
                    key: PropTypes.string,
                })
            ).isRequired,
        })
    ),
    autoFormat: PropTypes.bool,
    showPrintButton: PropTypes.bool,
    showCloseButton: PropTypes.bool,
    onClose: PropTypes.func,
};

/**
 * Helper function to open PrintPreview in a new window
 * @param {Object} data - The data to print
 * @param {String} title - Document title
 * @param {Object} options - Additional options (headerConfig, sections, etc.)
 */
export const openPrintPreview = (data, title = 'Document', options = {}) => {
    // Create a new window
    const printWindow = window.open('', '_blank', 'width=900,height=700');

    if (!printWindow) {
        alert('Please allow popups for this website to open print preview');
        return;
    }

    // Prepare the HTML content
    const htmlContent = `
        <!DOCTYPE html>
        <html lang="en">
        <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>${title} - Print Preview</title>
            <link rel="stylesheet" href="/static/css/main.css">
            <style>
                body {
                    margin: 0;
                    padding: 0;
                    font-family: Arial, sans-serif;
                }
                
                .print-preview-container {
                    max-width: 900px;
                    margin: 0 auto;
                    padding: 20px;
                }
                
                .print-preview-actions {
                    position: sticky;
                    top: 0;
                    background: white;
                    padding: 15px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                    z-index: 1000;
                    text-align: center;
                    margin-bottom: 20px;
                }
                
                .print-preview-content {
                    background: white;
                    padding: 40px;
                    box-shadow: 0 0 10px rgba(0,0,0,0.1);
                }
                
                .print-preview-header {
                    text-align: center;
                    margin-bottom: 20px;
                    padding-bottom: 20px;
                    border-bottom: 3px solid #1890ff;
                }
                
                .print-preview-logo img {
                    max-height: 80px;
                    margin-bottom: 10px;
                }
                
                .print-preview-hospital-name {
                    font-size: 24px;
                    font-weight: bold;
                    color: #1890ff;
                    margin: 10px 0;
                }
                
                .print-preview-subtitle {
                    font-size: 14px;
                    color: #666;
                    margin: 5px 0;
                }
                
                .print-preview-contact-info {
                    display: flex;
                    justify-content: center;
                    flex-wrap: wrap;
                    gap: 15px;
                    font-size: 12px;
                    color: #666;
                    margin-top: 10px;
                }
                
                .print-preview-document-title {
                    font-size: 20px;
                    font-weight: bold;
                    text-align: center;
                    margin: 20px 0 10px 0;
                    color: #333;
                }
                
                .print-preview-print-date {
                    text-align: center;
                    font-size: 12px;
                    color: #999;
                    margin-bottom: 30px;
                }
                
                .print-preview-section {
                    margin-bottom: 30px;
                    page-break-inside: avoid;
                }
                
                .print-preview-section-title {
                    font-size: 16px;
                    font-weight: bold;
                    color: #1890ff;
                    margin-bottom: 10px;
                    padding-bottom: 5px;
                    border-bottom: 2px solid #e8e8e8;
                }
                
                .print-preview-table {
                    width: 100%;
                    border-collapse: collapse;
                    margin-top: 10px;
                }
                
                .print-preview-table tr {
                    border-bottom: 1px solid #f0f0f0;
                }
                
                .print-preview-table td {
                    padding: 10px;
                    vertical-align: top;
                }
                
                .print-preview-label {
                    width: 35%;
                    color: #666;
                    font-weight: 600;
                }
                
                .print-preview-value {
                    width: 65%;
                    color: #333;
                }
                
                .print-preview-footer {
                    margin-top: 50px;
                    padding-top: 30px;
                    border-top: 2px solid #e8e8e8;
                }
                
                .print-preview-signature-section {
                    display: flex;
                    justify-content: space-around;
                    margin-bottom: 30px;
                }
                
                .print-preview-signature-box {
                    text-align: center;
                }
                
                .print-preview-signature-line {
                    width: 200px;
                    height: 1px;
                    background: #333;
                    margin: 40px auto 10px;
                }
                
                .print-preview-footer-branding {
                    text-align: center;
                    font-size: 11px;
                    color: #999;
                }
                
                /* Data Table Styles */
                .print-preview-data-table {
                    width: 100%;
                    border-collapse: collapse;
                    margin-top: 10px;
                    font-size: 12px;
                }
                
                .print-preview-data-table th,
                .print-preview-data-table td {
                    border: 1px solid #e8e8e8;
                    padding: 8px 12px;
                    text-align: left;
                    vertical-align: top;
                }
                
                .print-preview-data-table th {
                    background-color: #fafafa;
                    font-weight: 600;
                    color: #333;
                }
                
                .print-preview-data-table tr:nth-child(even) {
                    background-color: #fafafa;
                }
                
                @media print {
                    .no-print,
                    .print-preview-actions {
                        display: none !important;
                    }
                    
                    .print-preview-container {
                        max-width: 100%;
                        padding: 0;
                    }
                    
                    .print-preview-content {
                        box-shadow: none;
                        padding: 20px;
                    }
                    
                    @page {
                        margin: 1cm;
                        size: A4;
                    }
                }
            </style>
        </head>
        <body>
            <div id="root"></div>
            <script>
                window.printData = ${JSON.stringify(data)};
                window.printTitle = ${JSON.stringify(title)};
                window.printOptions = ${JSON.stringify(options)};
            </script>
        </body>
        </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();

    // Wait for the window to load, then render the component
    printWindow.onload = () => {
        renderPrintPreviewInWindow(printWindow, data, title, options);
    };
};

/**
 * Render PrintPreview component in the new window
 */
const renderPrintPreviewInWindow = (printWindow, data, title, options) => {
    const doc = printWindow.document;
    const container = doc.getElementById('root');

    if (!container) return;

    // Create the HTML structure directly
    const headerConfig = options.headerConfig || {
        hospitalName: 'Prakruti Ayurvedic Health Center & Hospital',
        subtitle: 'A Project By Prakruti Ayurvedic Health Resort, Satara',
        address: 'Satara, Maharashtra, India',
        phone: '+91 9175242908',
        email: 'feelbetter@prakruti.health',
        website: 'www.prakruti.health',
        logo: '/prakruti-logo.png',
    };

    const formatValue = (value) => {
        if (value === null || value === undefined || value === '') return '—';
        if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : '—';
        if (typeof value === 'object') return JSON.stringify(value, null, 2);
        if (moment(value, moment.ISO_8601, true).isValid()) {
            return moment(value).format('DD MMM YYYY, hh:mm A');
        }
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        return value.toString();
    };

    const formatLabel = (key) => {
        return key
            .replace(/([A-Z])/g, ' $1')
            .replace(/_/g, ' ')
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
    };

    // Generate sections
    let sectionsHTML = '';

    if (options.sections) {
        options.sections.forEach((section, index) => {
            if (section.type === 'table') {
                sectionsHTML += `
                    <div class="print-preview-section">
                        <h3 class="print-preview-section-title">${section.title}</h3>
                        ${section.description ? `<p class="print-preview-section-description">${section.description}</p>` : ''}
                        <div class="print-preview-section-content">
                            <table class="print-preview-data-table">
                                <thead>
                                    <tr>
                                        ${section.headers.map(header => `<th>${header}</th>`).join('')}
                                    </tr>
                                </thead>
                                <tbody>
                                    ${section.data.map(row => `
                                        <tr>
                                            ${row.map(cell => `<td>${cell}</td>`).join('')}
                                        </tr>
                                    `).join('')}
                                </tbody>
                            </table>
                        </div>
                    </div>
                `;
            } else {
                sectionsHTML += `
                    <div class="print-preview-section">
                        <h3 class="print-preview-section-title">${section.title}</h3>
                        ${section.description ? `<p class="print-preview-section-description">${section.description}</p>` : ''}
                        <div class="print-preview-section-content">
                            <table class="print-preview-table">
                                <tbody>
                                    ${section.fields.map(field => `
                                        <tr>
                                            <td class="print-preview-label"><strong>${field.label}</strong></td>
                                            <td class="print-preview-value">${field.value}</td>
                                        </tr>
                                    `).join('')}
                                </tbody>
                            </table>
                        </div>
                    </div>
                `;
            }
        });
    } else {
        // Auto-generate from data
        const entries = Object.entries(data);
        sectionsHTML += `
            <div class="print-preview-section">
                <h3 class="print-preview-section-title">Document Data</h3>
                <div class="print-preview-section-content">
                    <table class="print-preview-table">
                        <tbody>
                            ${entries.map(([key, value]) => `
                                <tr>
                                    <td class="print-preview-label"><strong>${formatLabel(key)}</strong></td>
                                    <td class="print-preview-value">${formatValue(value)}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </div>
            </div>
        `;
    }

    container.innerHTML = `
        <div class="print-preview-container">
            <div class="print-preview-actions no-print">
                <button onclick="window.print()" style="padding: 10px 20px; font-size: 16px; cursor: pointer; background: #1890ff; color: white; border: none; border-radius: 4px; margin-right: 10px;">
                    🖨️ Print Document
                </button>
                <button onclick="window.close()" style="padding: 10px 20px; font-size: 16px; cursor: pointer; background: #f0f0f0; color: #333; border: none; border-radius: 4px;">
                    ✖️ Close
                </button>
            </div>
            
            <div class="print-preview-content">
                <div class="print-preview-header">
                    ${headerConfig.logo ? `<div class="print-preview-logo"><img src="${headerConfig.logo}" alt="Logo" /></div>` : ''}
                    <div class="print-preview-header-content">
                        <h1 class="print-preview-hospital-name">${headerConfig.hospitalName}</h1>
                        ${headerConfig.subtitle ? `<p class="print-preview-subtitle">${headerConfig.subtitle}</p>` : ''}
                        <div class="print-preview-contact-info">
                            ${headerConfig.address ? `<span class="print-preview-contact-item">📍 ${headerConfig.address}</span>` : ''}
                            ${headerConfig.phone ? `<span class="print-preview-contact-item">📞 ${headerConfig.phone}</span>` : ''}
                            ${headerConfig.email ? `<span class="print-preview-contact-item">✉️ ${headerConfig.email}</span>` : ''}
                            ${headerConfig.website ? `<span class="print-preview-contact-item">🌐 ${headerConfig.website}</span>` : ''}
                        </div>
                    </div>
                </div>
                
                <div class="print-preview-divider" style="height: 2px; background: #e8e8e8; margin: 20px 0;"></div>
                
                <div class="print-preview-title-section">
                    <h2 class="print-preview-document-title">${title}</h2>
                    <p class="print-preview-print-date">Printed on: ${moment().format('DD MMMM YYYY, hh:mm A')}</p>
                </div>
                
                <div class="print-preview-body">
                    ${sectionsHTML}
                </div>
                
                <div class="print-preview-footer">
                    <div class="print-preview-signature-section">
                        <div class="print-preview-signature-box">
                            <div class="print-preview-signature-line"></div>
                            <p>Authorized Signature</p>
                        </div>
                        <div class="print-preview-signature-box">
                            <div class="print-preview-signature-line"></div>
                            <p>Date & Time</p>
                        </div>
                    </div>
                    <div class="print-preview-footer-branding">
                        <p>This is a computer-generated document from ${headerConfig.hospitalName}</p>
                    </div>
                </div>
            </div>
        </div>
    `;
};

export default PrintPreview;
